import clsx from 'clsx';
import { memo } from 'react';

import { type HeaderProps } from '../../types/SharedHeaderProps';
import { Actions } from '../Actions/Actions';
import { IconButton } from '../Button/subcomponents/IconButton';
import { Layout } from '../Layout/Layout';
import { StatusBadge } from '../StatusBadge/StatusBadge';
import { Text } from '../Text/Text';
import './Header.css';

export const Header = memo(
  ({
    actions,
    actionsContent,
    closeAction,
    moreActions,
    size = 'm',
    statusBadge,
    subtitle,
    subtitleId,
    title,
    titleAction,
    titleAs = 'h3',
    titleId,
    toggleAction,
  }: HeaderProps) => {
    const actionSize = size === 'l' ? 'm' : 's';
    let spacing: 'xs' | 'm' | 's' = 's';
    if (size === 's') {
      spacing = 'xs';
    } else if (size === 'l') {
      spacing = 'm';
    }
    const titleSpacing = size === 's' ? 'xs' : 's';
    let titleVariant: 'body-semi-l' | 'heading-s' | 'heading-xs' = 'heading-xs';
    if (size === 's') {
      titleVariant = 'body-semi-l';
    } else if (size === 'l') {
      titleVariant = 'heading-s';
    }
    return (
      <Layout
        align="start"
        className={clsx('eds-header', `eds-header--size-${size}`)}
        justify="between"
      >
        <Layout
          align="center"
          className="eds-header--content"
          flex="1"
          spacing={spacing}
        >
          {toggleAction?.icon && (
            <IconButton
              icon={{
                key: toggleAction.icon.key,
                label: toggleAction.icon.label,
                size: 's',
              }}
              label={toggleAction.label}
              size={actionSize}
              tooltip={toggleAction.tooltip ?? { text: toggleAction.label }}
              variant="tertiary"
              onClick={toggleAction.onClick}
            />
          )}
          <Layout
            className="eds-header--title-block"
            direction="column"
            flex="1"
            justify="center"
            spacing={titleSpacing}
          >
            <Layout
              align="center"
              className="eds-header--title-row"
              spacing="s"
            >
              <Layout className="eds-header--title">
                <Text
                  as={titleAs}
                  id={titleId}
                  truncate={1}
                  variant={titleVariant}
                >
                  {title}
                </Text>
              </Layout>
              {statusBadge && (
                <Layout className="eds-header--status-badge">
                  <StatusBadge {...statusBadge} />
                </Layout>
              )}
              {/* Wrapped so the action keeps its size beside a title long
                  enough to consume the row. */}
              {titleAction?.icon && (
                <Layout>
                  <IconButton
                    icon={{
                      key: titleAction.icon.key,
                      label: titleAction.icon.label,
                      size: 's',
                    }}
                    label={titleAction.label}
                    size={actionSize}
                    tooltip={titleAction.tooltip ?? { text: titleAction.label }}
                    variant="tertiary"
                    onClick={titleAction.onClick}
                  />
                </Layout>
              )}
            </Layout>
            {subtitle && (
              <Layout className="eds-header--subtitle">
                <Text
                  as="h5"
                  id={subtitleId}
                  tone="subdued"
                  truncate={1}
                  variant={`body-${size}`}
                >
                  {subtitle}
                </Text>
              </Layout>
            )}
          </Layout>
        </Layout>
        <Layout align="center" spacing={spacing}>
          {Boolean(actions || moreActions) && (
            <Actions
              actions={actions ?? []}
              mode="icon"
              moreActions={moreActions}
              size={actionSize}
            />
          )}
          {actionsContent}
          {closeAction && (
            <IconButton
              icon={
                closeAction.icon ?? {
                  key: 'x',
                  label: closeAction.label,
                  size: 's',
                }
              }
              label={closeAction.label}
              size={size === 'l' ? 'm' : 's'}
              tooltip={closeAction.tooltip}
              variant="tertiary"
              onClick={closeAction.onClick}
            />
          )}
        </Layout>
      </Layout>
    );
  },
);

Header.displayName = 'Header';
